// Action definitions for the Sentient Orb
// Each action modifies the orb's particles in different ways

export interface OrbActionParams {
  time: number;
  particleCount: number;
  positions: Float32Array;
  originalPositions: Float32Array;
  distances: Float32Array;
  geometry: any;
  pointLight1: any;
  intensity?: number; // 0-1 for action intensity
  timeSincePulse?: number; // Time since last pulse was triggered (in seconds)
}

// IDLE: Gentle breathing effect - the orb at rest
export const idle = ({ time, particleCount, positions, originalPositions, geometry, pointLight1 }: OrbActionParams) => {
  const breathe = 1 + Math.sin(time * 2) * 0.03;
  
  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;
    positions[i3] = originalPositions[i3] * breathe;
    positions[i3 + 1] = originalPositions[i3 + 1] * breathe;
    positions[i3 + 2] = originalPositions[i3 + 2] * breathe;
  }
  
  geometry.attributes.position.needsUpdate = true;
  pointLight1.intensity = 2 + Math.sin(time * 2.5) * 0.3;
};

// TALK: Continuous wave animation - the orb is speaking
// Creates organic, flowing waves throughout the surface
export const talk = ({ 
  time, 
  particleCount, 
  positions, 
  originalPositions, 
  distances, 
  geometry, 
  pointLight1,
  intensity = 1
}: OrbActionParams) => {
  // Continuous waves - no pulse timing needed
  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;
    const dist = distances[i];
    
    const x = originalPositions[i3];
    const y = originalPositions[i3 + 1];
    const z = originalPositions[i3 + 2];
    
    // Create phase offsets for organic movement
    const phaseX = x * 1.5;
    const phaseY = y * 1.5;
    const phaseZ = z * 1.5;
    
    // Multiple overlapping waves
    const wave1 = Math.sin((dist * 2.5) - (time * 4.0) + phaseX);
    const wave2 = Math.sin((dist * 3.0) - (time * 5.0) + phaseY);
    const wave3 = Math.sin((dist * 3.5) - (time * 3.5) + phaseZ);
    
    // Combine waves
    const combinedWave = (wave1 * 0.4 + wave2 * 0.35 + wave3 * 0.25);
    
    // Apply displacement
    const displacement = combinedWave * 0.08 * intensity;
    const scaleFactor = 1.0 + displacement;
    
    positions[i3] = x * scaleFactor;
    positions[i3 + 1] = y * scaleFactor;
    positions[i3 + 2] = z * scaleFactor;
  }
  
  geometry.attributes.position.needsUpdate = true;
  pointLight1.intensity = 2.2 + Math.sin(time * 3.5) * (0.6 * intensity);
};

// EXCITED: More dramatic wave effect for emphasis or excitement
export const excited = ({ 
  time, 
  particleCount, 
  positions, 
  originalPositions, 
  distances, 
  geometry, 
  pointLight1,
  intensity = 1 
}: OrbActionParams) => {
  const waveSpeed = 3.5;
  const waveFrequency = 4.0;
  const waveAmplitude = 0.12 * intensity;
  
  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;
    const dist = distances[i];
    
    const wave = Math.sin((dist * waveFrequency) - (time * waveSpeed));
    const rippleEffect = 1 + (wave * waveAmplitude);
    
    positions[i3] = originalPositions[i3] * rippleEffect;
    positions[i3 + 1] = originalPositions[i3 + 1] * rippleEffect;
    positions[i3 + 2] = originalPositions[i3 + 2] * rippleEffect;
  }
  
  geometry.attributes.position.needsUpdate = true;
  pointLight1.intensity = 2 + Math.sin(time * 5) * (0.8 * intensity);
};

// Export action types
export type OrbAction = 'idle' | 'talk' | 'excited';

// Action map
export const orbActions = {
  idle,
  talk,
  excited
};